import { Record } from "immutable";
import { FlashLevelTypes } from "common/components/FlashAlert";
import { checkDate } from "common/helpers/Date";
import { Nullable } from "common/types";

/** The severities rsf-analytics stamps on a message (`Ecto.Enum` success/info/warning/danger). */
export type AppMessageLevel = Exclude<FlashLevelTypes, "update">;

export interface IAppMessageContentData {
  id: string;
  subject: string;
  body_html: string;
  level: AppMessageLevel;
  created_at: string;
}

export interface IAppMessageContentRecord extends Omit<IAppMessageContentData, "created_at"> {
  created_at: Date;
}

export class AppMessageContentRecord extends Record<IAppMessageContentRecord>({
  id: "",
  subject: "",
  body_html: "",
  level: "info",
  created_at: new Date(),
}) {}

export const appMessageContentFromJSON = (json: Partial<IAppMessageContentData>): AppMessageContentRecord => {
  const record: IAppMessageContentRecord = { ...(json as any) };

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  return new AppMessageContentRecord(record);
};

export interface IAppMessageRecipientData {
  id: string;
  app_message_id: string;
  action_at: Nullable<string>;
  dismissed_at: Nullable<string>;
  read_at: Nullable<string>;
  archived_at: Nullable<string>;
  created_at: string;
  app_message: IAppMessageContentData;
}

export interface IAppMessageRecipientRecord extends Omit<
  IAppMessageRecipientData,
  "app_message" | "action_at" | "dismissed_at" | "read_at" | "archived_at" | "created_at"
> {
  action_at: Nullable<Date>;
  dismissed_at: Nullable<Date>;
  read_at: Nullable<Date>;
  archived_at: Nullable<Date>;
  created_at: Date;
  app_message: AppMessageContentRecord;
}

export class AppMessageRecipientRecord extends Record<IAppMessageRecipientRecord>({
  id: "",
  app_message_id: "",
  action_at: null,
  dismissed_at: null,
  read_at: null,
  archived_at: null,
  created_at: new Date(),
  app_message: new AppMessageContentRecord(),
}) {}

export const appMessageRecipientFromJSON = (json: Partial<IAppMessageRecipientData>): AppMessageRecipientRecord => {
  const record: IAppMessageRecipientRecord = { ...(json as any) };

  if (json.action_at) {
    record.action_at = checkDate(json.action_at);
  }

  if (json.dismissed_at) {
    record.dismissed_at = checkDate(json.dismissed_at);
  }

  if (json.read_at) {
    record.read_at = checkDate(json.read_at);
  }

  if (json.archived_at) {
    record.archived_at = checkDate(json.archived_at);
  }

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  record.app_message = appMessageContentFromJSON(json.app_message ?? {});

  return new AppMessageRecipientRecord(record);
};

/**
 * The server counts a toast's "View" (`action_at`) as having seen the message.
 *
 * @param message
 * @returns
 */
export const isRead = (message: Nullable<AppMessageRecipientRecord>): boolean => {
  if (!message) return false;

  return Boolean(message.read_at) || Boolean(message.action_at);
};
